import React from 'react';
import { motion } from "framer-motion";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Calendar, Clock, ArrowRight, Rss } from "lucide-react";

const posts = [
  {
    title: "The Future of Cross-Border Payments",
    excerpt: "Exploring how blockchain and instant settlement networks are dramatically lowering costs and increasing speed for international transactions.",
    category: "Industry Insights",
    date: "Mar 15, 2026",
    readTime: "5 min read",
    gradient: "from-blue-500/20 to-violet-500/20"
  },
  {
    title: "PCI DSS 4.0: What You Need to Know",
    excerpt: "A comprehensive guide to the upcoming changes in payment security standards and how Luxkgate handles compliance for you.",
    category: "Security",
    date: "Mar 10, 2026",
    readTime: "8 min read",
    gradient: "from-emerald-500/20 to-teal-500/20"
  },
  {
    title: "Optimizing Checkout Conversion Rates",
    excerpt: "Learn the exact UI patterns and performance tweaks that can help your merchants increase their cart conversion by up to 15%.",
    category: "Best Practices",
    date: "Mar 02, 2026",
    readTime: "6 min read",
    gradient: "from-amber-500/20 to-orange-500/20"
  },
  {
    title: "Introducing Smart Routing API v2",
    excerpt: "We just released a massive upgrade to our smart routing engine, allowing customized cascading across infinite provider nodes.",
    category: "Product Updates",
    date: "Feb 24, 2026",
    readTime: "4 min read",
    gradient: "from-blue-500/20 to-cyan-500/20"
  },
  {
    title: "Managing Chargebacks at Scale",
    excerpt: "Strategies for high-volume resellers to detect friendly fraud early and win more dispute cases effortlessly.",
    category: "Guides",
    date: "Feb 18, 2026",
    readTime: "7 min read",
    gradient: "from-rose-500/20 to-pink-500/20"
  },
  {
    title: "Building White-Label Apps with React",
    excerpt: "A deep dive into our frontend architecture and how we allow engines to completely theme our checkout elements.",
    category: "Engineering",
    date: "Feb 10, 2026",
    readTime: "10 min read",
    gradient: "from-violet-500/20 to-fuchsia-500/20"
  }
];

const Blog = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <Rss className="w-4 h-4 text-violet-400" />
            <span className="text-sm text-slate-400">Updates & Resources</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            The Luxkgate <br />
            <span className="gradient-text">Blog</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Catch up on the latest product announcements, industry trends, and engineering insights from our team.
          </p>
        </motion.div>

        {/* Featured Post */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-16"
        >
          <Card className="bg-slate-900/40 border-slate-800/50 hover:border-slate-700 transition-colors overflow-hidden group cursor-pointer">
            <div className="lg:flex">
              <div className="lg:w-1/2 p-12 lg:p-16 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-slate-800/50 bg-gradient-to-tr from-blue-900/20 to-violet-900/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-32 bg-blue-500/10 blur-[100px] rounded-full mix-blend-screen" />
                <div className="relative z-10">
                  <span className="text-sm font-semibold px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 mb-6 inline-block border border-blue-500/30">
                    Featured
                  </span>
                  <h2 className="text-3xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">
                    The Luxkgate 2026 Payments Report is Here
                  </h2>
                  <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                    We analyzed over 100 million transactions to uncover the dominant trends shaping global e-commerce. Download our free report to see our predictions.
                  </p>
                  <div className="flex items-center gap-6 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> Mar 20, 2026</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> 12 min read</span>
                  </div>
                </div>
              </div>
              <div className="lg:w-1/2 flex items-center justify-center p-12 relative overflow-hidden bg-slate-950">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aC0ydi00aDJ2LTJoNHYyaDJ2NGgtMnYyaC00di0yem0wLThoLTJ2LTJoMnYyem0wIDEwaC0ydi0yaDJ2MnptLTEwLTRoLTJ2LTJoMnYyem0wIDRoLTJ2LTJoMnYyem0wLThoLTJ2LTJoMnYyem0xMCA4aC0ydi0yaDJ2MnptMTAtNGgtMnYtMmgydjJ6bTAgNGgtMnYtMmgydjJ6bTAtOGgtMnYtMmgydjJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
                <Button className="bg-white hover:bg-slate-100 text-slate-900 px-8 py-6 text-lg relative z-10">
                  Read Article <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Blog Post Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-colors h-full flex flex-col group cursor-pointer overflow-hidden">
                <div className={`h-40 w-full bg-gradient-to-tr ${post.gradient} relative`}>
                  <div className="absolute inset-0 bg-slate-950/40 mix-blend-multiply" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-white/5 transition-opacity duration-300" />
                </div>
                <CardContent className="p-8 flex-1 flex flex-col relative bg-slate-900/50 -mt-4 rounded-t-2xl z-10 border-t border-slate-800/50">
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6 flex-1 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-6 border-t border-slate-800/50 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {post.date}</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.readTime}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-16">
          <Button variant="outline" className="bg-transparent border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 hover:border-slate-500 px-8 py-6 text-base rounded-xl">
            Load More Posts
          </Button>
        </div>

      </div>
    </div>
  )
}

export default Blog;